import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _AbCalendarDay;
/*
 *                     NOTICE
 *            COPYRIGHT 2021 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { ValueFormatAttributesUtils } from "abweb/dto/ds/ValueFormatAttributesUtils";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { DateTimeUtils } from "abweb/utils/date/DateTimeUtils";
import { DateUtils } from "abweb/utils/DateUtils";

/** This represents a month/day/year with no time information.
 * Addresses issues with trying to represent dates using JavaScript Date which includes time.
 * With Date, one must be very careful with local vs. UTC time to avoid shifting day by one.
 * We have had so many bugs like that, this class is intended to implement the details of keeping track and make it hard to get wrong.
 */
export class AbCalendarDay {
  /* Checks that the given string has the from of YYYY-MM-DD, which is the prefix of the standard ISO date format.
      This is also the format used by Metadata Hub Entity API JSON.
   */
  static isValidSerialization(serialized) {
    return ClassUtils.isInstanceOf(DateTimeUtils.DATE_YYYY_MM_DD.parse(serialized), Date);
  }

  /* Construct a day from a YYYY-MM-DD string, which is the prefix of the standard ISO date format.
      This is also the format used by Metadata Hub Entity API JSON.
   */
  static fromSerialized(serialized) {
    const dt = DateTimeUtils.DATE_YYYY_MM_DD.parse(serialized);
    if (ClassUtils.isInstanceOf(dt, Date)) {
      return AbCalendarDay.fromLocalDate(dt);
    }
    if (dt == null) {
      throw new Error(`Error: '${serialized}' is not a valid date serialization of the form ${DateTimeUtils.DATE_YYYY_MM_DD.parseContext.parseFormat}`);
    }
    if (ClassUtils.isInstanceOf(dt, Error)) {
      throw dt;
    }
    // defensive
    throw new Error(`Unknown type of parse return value: ${typeof dt}`);
  }

  /**
   * Construct a day using the month/day/year of 'date' in the local time zone.
   * Use this when taking dates from things like Kendo calendar controls that work in local time
   */

  // use static constructor methods instead
  constructor(_data) {
    _defineProperty(this, "inRange", (earliest, latest) => {
      return this.compare(earliest) >= 0 && this.compare(latest) <= 0;
    });
    this._data = _data;
  }
  get year() {
    return this._data.year;
  }
  get month() {
    return this._data.month;
  }
  get day() {
    return this._data.day;
  }

  /** Editing UI components (e.g. Kendo, many of ours) work on dates at local midnight.
   * Also helpful for formatting and display of dates in local time.
   */
  get localMidnightDate() {
    return DateUtils.newLocalDateWithFullYear(this.year, this.month - 1, this.day); // month index starts at 0.
  }

  /** We often talk to server using UTC midnight dates. And possibly some UI might work in UTC dates.. */
  get UTCMidnightDate() {
    return DateUtils.newUTCDateWithFullYear(this.year, this.month - 1, this.day); // month index starts at 0.
  }

  /** Returns the standard over the wire serialization of the day, which is YYYY-MM-DD */
  serialize() {
    // Could use DateTimeUtils.FORMAT_DATE_YYYY_MM_DD.format() to format, but already have year, month, day available
    return `${this.year.toString().padStart(4, "0")}-${this.month.toString().padStart(2, "0")}-${this.day.toString().padStart(2, "0")}`;
  }

  /**
   * @deprecated
   * Redundant function. Month day and year are directly accessible from AbCalendarDay now.
   */
  monthDayYear() {
    return {
      year: this.year,
      month: this.month,
      day: this.day
    };
  }
  getFormattedValue(formatOptions) {
    return ValueFormatAttributesUtils.formatDateAsString(this.localMidnightDate, formatOptions,
    // try to calculated dateTimeFormatter to backing formatOptions.dateTimeFormatter if formatOptions is provided.
    // Big win as creating a formatter is 50x more expensive than using it to format
    !!formatOptions);
  }

  /**
   * @return true if other is the same date as this
   * @param other
   */
  equals(other) {
    return other != null && this.year === other.year && this.month === other.month && this.day === other.day;
  }
  compare(other) {
    const thisDate = this.UTCMidnightDate;
    const otherDate = AbCalendarDay.fromData(other).UTCMidnightDate;
    if (thisDate < otherDate) {
      return -1;
    }
    if (thisDate > otherDate) {
      return 1;
    }
    return 0;
  }
}
_AbCalendarDay = AbCalendarDay;
_defineProperty(AbCalendarDay, "fromLocalDate", date => {
  // pick off local time zone month/day/year. Then build internal string representation from those.
  const monthIndex = date.getMonth(); // zero based
  const day = date.getDate();
  const year = date.getFullYear();
  if (Number.isNaN(monthIndex) || Number.isNaN(day) || Number.isNaN(year)) {
    throw new Error("Error: AbCalendarDay::fromLocalDate passed invalid date object.");
  }
  return new _AbCalendarDay({
    year,
    month: monthIndex + 1,
    day
  });
});
/**
 * Construct a day using the month/day/year of 'date' in the UTC time zone.
 */
_defineProperty(AbCalendarDay, "fromUTCDate", date => {
  return new _AbCalendarDay({
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate()
  });
});
_defineProperty(AbCalendarDay, "fromData", data => {
  return new _AbCalendarDay({
    year: data.year,
    month: data.month,
    day: data.day
  });
});
//# sourceMappingURL=AbCalendarDay.js.map
